import { SearchTextField } from "@/controls/quick-filter-textfield/SearchTextField";
import { PourbackReason } from "@/pages/caster/models/catalogs/pourback-reasons";
import { useGridStyles } from "@/pages/laboratory/styles/gridStyles";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import React, { useState, useRef } from "react";
import { UpsertPourbackReason } from "./UpsertPourbackReason";
import { useTableData } from "./hooks/useTableData";

type Props = {
	pourbackReasons: PourbackReason[];
	isLoading: boolean;
	onRefresh: () => void;
};

export const TablePourbackReasons: React.FC<Props> = ({
	pourbackReasons,
	isLoading,
	onRefresh,
}) => {
	const classes = useGridStyles();
	const [quickFilterText, setQuickFilterText] = useState("");
	const gridRef = useRef<AgGridReact>(null);

	// Modal state
	const [modalState, setModalState] = useState<
		"create" | "edit" | "delete" | null
	>(null);
	const [selectedPourbackReason, setSelectedPourbackReason] =
		useState<PourbackReason | null>(null);

	const handleCreateClick = () => {
		setSelectedPourbackReason(null);
		setModalState("create");
	};

	const handleEditClick = (pourbackReasonId: number) => {
		const pourbackReason = pourbackReasons.find(
			(p) => p.PourbackReasonId === pourbackReasonId,
		);
		if (pourbackReason) {
			setSelectedPourbackReason(pourbackReason);
			setModalState("edit");
		}
	};

	const handleDeleteClick = (pourbackReasonId: number) => {
		const pourbackReason = pourbackReasons.find(
			(p) => p.PourbackReasonId === pourbackReasonId,
		);
		if (pourbackReason) {
			setSelectedPourbackReason(pourbackReason);
			setModalState("delete");
		}
	};

	const handleModalClose = () => {
		setModalState(null);
		setSelectedPourbackReason(null);
	};

	const handleModalSuccess = () => {
		onRefresh();
		handleModalClose();
	};

	const { columns } = useTableData({
		onDeleteButtonClick: handleDeleteClick,
		onEditButtonClick: handleEditClick,
	});

	React.useEffect(() => {
		if (gridRef.current) {
			if (isLoading) {
				gridRef.current.api?.showLoadingOverlay();
			} else {
				gridRef.current.api?.hideOverlay();
			}
		}
	}, [isLoading]);

	return (
		<>
			<Grid size={{ xs: 12 }} container justifyContent="center" spacing={1}>
				<Grid size={{ xs: 12, md: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Pourback Reasons
					</Typography>
				</Grid>

				<Grid
					size={{ xs: 12 }}
					container
					justifyContent="flex-end"
					alignContent="center"
				>
					<Grid size={{ xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search pourback reasons"
						/>
					</Grid>
					<Grid
						size={{ xs: 12, md: 12 }}
						style={{ height: "68vh" }}
						className={classes.borderAgGridTable}
					>
						<div style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								ref={gridRef}
								rowData={pourbackReasons}
								columnDefs={columns}
								getRowId={(params) => params.data.PourbackReasonId}
								suppressContextMenu
								onRowDoubleClicked={(params) => {
									handleEditClick(params.data.PourbackReasonId);
								}}
								rowSelection="single"
								quickFilterText={quickFilterText}
							/>
						</div>
					</Grid>

					<Grid size={{ xs: 12, md: "auto" }} style={{ marginTop: 10 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={{ minWidth: 250 }}
							onClick={handleCreateClick}
						>
							NEW POURBACK REASON
						</Button>
					</Grid>
				</Grid>
			</Grid>

			{modalState && (
				<UpsertPourbackReason
					open={modalState !== null}
					mode={modalState}
					pourbackReason={selectedPourbackReason}
					onClose={handleModalClose}
					onSuccess={handleModalSuccess}
				/>
			)}
		</>
	);
};
